import { useEffect, useState } from 'react'
import { useSearchParams, Link } from 'react-router-dom'

type TabKey = 'importar' | 'erp' | 'webhook' | 'branding' | 'usuarios' | 'filiais' | 'conta' | 'sobre'

const TABS: { key: TabKey; label: string }[] = [
  { key: 'importar',     label: 'Importar Planilha' },
  { key: 'erp',          label: 'Integração ERP' },
  { key: 'webhook',      label: 'Webhook' },
  { key: 'branding',     label: 'Visual da Marca' },
  { key: 'usuarios',     label: 'Usuários' },
  { key: 'filiais',      label: 'Filiais' },
  { key: 'conta',        label: 'Conta' },
  { key: 'sobre',        label: 'Sobre' },
]
import AppLayout from '../../components/layout/AppLayout'
import FormInput from '../../components/ui/FormInput'
import { APP_VERSION, GIT_COMMIT, BUILD_DATE } from '../../version'
import Alert from '../../components/ui/Alert'
import { supabase } from '../../lib/supabase'
import { useSync } from '../../contexts/SyncContext'
import { useToast } from '../../hooks/useToast'
import { useAuth } from '../../hooks/useAuth'
import { useTeamUsers } from '../../hooks/useTeamUsers'
import { importProducts, parseFilePreview, type ImportFieldMap, type ImportResult, type ParsedRow } from '../../lib/productImport'
import { applyBranding, resetBranding } from '../../lib/branding'
import FiliaisSettings from '../../components/settings/FiliaisSettings'
import SyncHealthCard from '../../components/settings/SyncHealthCard'
import { logAudit } from '../../lib/audit'
import type { ErpProvider } from '../../types'

export default function SettingsPage() {
  const [token, setToken] = useState('')
  const [savingToken, setSavingToken] = useState(false)
  const [tokenMsg, setTokenMsg] = useState<{ type: 'error' | 'success'; text: string } | null>(null)
  const [v3ConnectedAt, setV3ConnectedAt] = useState<string | null>(null)
  const [v3CompanyId, setV3CompanyId] = useState<string | null>(null)
  const [v3Msg, setV3Msg] = useState<{ type: 'error' | 'success'; text: string } | null>(null)
  const [tinyClientId, setTinyClientId] = useState('')
  const [tinyClientSecret, setTinyClientSecret] = useState('')
  const [savingCredentials, setSavingCredentials] = useState(false)
  const [credentialsMsg, setCredentialsMsg] = useState<{ type: 'error' | 'success'; text: string } | null>(null)
  const [erpProvider, setErpProvider] = useState<ErpProvider>('none')
  const [salesWindow, setSalesWindow] = useState<number>(6)
  const [savingWindow, setSavingWindow] = useState(false)
  const [cadastroRecEnabled, setCadastroRecEnabled] = useState<boolean>(true)
  const [savingCadastroRec, setSavingCadastroRec] = useState(false)

  const [webhookCopied, setWebhookCopied] = useState(false)

  // ── Branding ──────────────────────────────────────────────────────────────
  const [brandColor, setBrandColor] = useState('#647B45')
  const [brandLogoUrl, setBrandLogoUrl] = useState<string | null>(null)
  const [brandLogoFile, setBrandLogoFile] = useState<File | null>(null)
  const [brandLogoPreview, setBrandLogoPreview] = useState<string | null>(null)
  const [brandCompanyId, setBrandCompanyId] = useState<string | null>(null)
  const [savingBrand, setSavingBrand] = useState(false)
  const [brandMsg, setBrandMsg] = useState<{ type: 'error' | 'success'; text: string } | null>(null)

  const { productsSyncing: syncing, startProductsSync, productsResult: result } = useSync()
  const syncNow = (testOnly = false) => startProductsSync(testOnly)
  const team = useTeamUsers()
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<'operator' | 'admin'>('operator')
  const [inviting, setInviting] = useState(false)
  const isAdmin = team.myRole === 'admin' || team.myRole === 'superadmin'

  // ── Conta (troca de senha) ───────────────────────────────────────────────
  const { user } = useAuth()
  const [newPwd, setNewPwd] = useState('')
  const [confirmPwd, setConfirmPwd] = useState('')
  const [savingPwd, setSavingPwd] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [deletingAccount, setDeletingAccount] = useState(false)

  async function handleChangePassword() {
    if (newPwd.length < 6) { notify('error', 'Senha deve ter pelo menos 6 caracteres.'); return }
    if (newPwd !== confirmPwd) { notify('error', 'As senhas não conferem.'); return }
    setSavingPwd(true)
    const { error } = await supabase.auth.updateUser({ password: newPwd })
    setSavingPwd(false)
    if (error) { notify('error', error.message); return }
    setNewPwd(''); setConfirmPwd('')
    notify('success', 'Senha alterada com sucesso.')
  }

  // ── LGPD: exportar e excluir conta ───────────────────────────────────────
  async function handleExportData() {
    if (!user) return
    // Confirmação: o backup baixa o histórico COMPLETO (inclui a tabela de vendas, a maior
    // do banco) e pode transferir bastante dado de uma vez (egress). Gate evita disparo
    // acidental/repetido de uma ação que deveria ser rara. Os dados seguem completos (LGPD).
    const ok = confirm(
      'Exportar todos os seus dados?\n\n' +
      '• O arquivo inclui o histórico completo (produtos, vendas, pedidos) e pode ficar grande.\n' +
      '• Use apenas quando realmente precisar de uma cópia dos seus dados.'
    )
    if (!ok) return
    setExporting(true)
    try {
      // Profile próprio
      const [{ data: profile }, { data: companyData }] = await Promise.all([
        supabase.from('profiles').select('*').eq('id', user.id).maybeSingle(),
        supabase.from('profiles').select('company_id').eq('id', user.id).maybeSingle(),
      ])
      const companyId = (companyData?.company_id as string | undefined) ?? null

      // Dados da empresa (acessíveis via RLS — só vê a própria)
      const company = companyId
        ? (await supabase.from('companies').select('*').eq('id', companyId).maybeSingle()).data
        : null

      // Dados operacionais (escopo do usuário/empresa via RLS)
      const [products, suppliers, sales, purchaseOrders, purchaseOrderItems] = await Promise.all([
        supabase.from('products').select('*'),
        supabase.from('suppliers').select('*'),
        supabase.from('sales').select('*'),
        supabase.from('purchase_orders').select('*'),
        supabase.from('purchase_order_items').select('*'),
      ])

      const payload = {
        exported_at: new Date().toISOString(),
        exported_by: { id: user.id, email: user.email ?? null },
        profile,
        company,
        products: products.data ?? [],
        suppliers: suppliers.data ?? [],
        sales: sales.data ?? [],
        purchase_orders: purchaseOrders.data ?? [],
        purchase_order_items: purchaseOrderItems.data ?? [],
      }

      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `bx-export-${user.email?.split('@')[0] ?? 'me'}-${new Date().toISOString().slice(0, 10)}.json`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)

      notify('success', 'Exportação concluída — arquivo baixado.')
    } catch (e) {
      notify('error', `Falha ao exportar: ${(e as Error).message}`)
    } finally {
      setExporting(false)
    }
  }

  async function handleSalesWindowChange(months: number) {
    if (!brandCompanyId) return
    const previous = salesWindow
    setSalesWindow(months) // optimistic
    setSavingWindow(true)
    const { error } = await supabase
      .from('companies')
      .update({ sales_window_months: months })
      .eq('id', brandCompanyId)
    setSavingWindow(false)
    if (error) {
      setSalesWindow(previous)
      notify('error', `Falha ao salvar janela: ${error.message}`)
      return
    }
    void logAudit('company.sales_window_change', {
      target_type: 'company',
      target_id: brandCompanyId,
      payload: { from: previous, to: months },
    })
    notify('success', `Janela atualizada para ${months} ${months === 1 ? 'mês' : 'meses'}. Próximo sync respeitará.`)
  }

  async function handleToggleCadastroRec(next: boolean) {
    if (!brandCompanyId) return
    const previous = cadastroRecEnabled
    setCadastroRecEnabled(next) // optimistic
    setSavingCadastroRec(true)
    const { error } = await supabase
      .from('companies')
      .update({ cadastro_recebimento_enabled: next })
      .eq('id', brandCompanyId)
    setSavingCadastroRec(false)
    if (error) {
      setCadastroRecEnabled(previous)
      notify('error', `Falha ao salvar: ${error.message}`)
      return
    }
    void logAudit('company.cadastro_recebimento_toggle', {
      target_type: 'company',
      target_id: brandCompanyId,
      payload: { from: previous, to: next },
    })
    notify('success', next ? 'Cadastro e Recebimento ativado. Recarregue a página para ver a aba no menu.' : 'Cadastro e Recebimento desativado.')
  }

  async function handleRequestDeletion() {
    const ok = confirm(
      'Excluir sua conta?\n\n' +
      '• Você será desconectado imediatamente.\n' +
      '• Sua conta entra em grace period de 30 dias antes da exclusão definitiva.\n' +
      '• Você pode reverter o pedido a qualquer momento durante esse prazo (basta fazer login).\n\n' +
      'Confirmar?'
    )
    if (!ok) return
    setDeletingAccount(true)
    const { error } = await supabase.rpc('request_account_deletion')
    if (error) {
      setDeletingAccount(false)
      notify('error', `Falha ao solicitar exclusão: ${error.message}`)
      return
    }
    // Audit log antes do logout (depois sai)
    await logAudit('account.delete_request', { target_type: 'profile', target_id: user?.id ?? undefined })
    // Sign out — após login, o useOnboarding detecta deletion_requested_at e mostra DeletedAccountPage
    await supabase.auth.signOut()
    // Fallback caso onAuthStateChange não dispare reload
    window.location.href = '/login'
  }

  async function handleInvite() {
    if (!inviteEmail.trim()) { notify('error', 'Informe um e-mail.'); return }
    setInviting(true)
    const r = await team.invite(inviteEmail.trim(), inviteRole)
    setInviting(false)
    if (!r.ok) { notify('error', r.error ?? 'Falha ao convidar'); return }
    notify('success', `Convite enviado para ${inviteEmail.trim()}.`)
    void logAudit('user.invite', {
      target_type: 'email',
      target_id: inviteEmail.trim(),
      payload: { role: inviteRole },
    })
    setInviteEmail('')
    setInviteRole('operator')
  }

  async function handleRemoveUser(userId: string, email: string) {
    if (!confirm(`Remover acesso de ${email}? Esta ação é irreversível.`)) return
    const r = await team.remove(userId)
    if (!r.ok) { notify('error', r.error ?? 'Falha ao remover'); return }
    notify('success', `${email} removido.`)
    void logAudit('user.remove', {
      target_type: 'auth_user',
      target_id: userId,
      payload: { email },
    })
  }
  const [searchParams, setSearchParams] = useSearchParams()
  const tab = (searchParams.get('tab') as TabKey) || 'importar'
  const setTab = (k: TabKey) => setSearchParams({ tab: k }, { replace: true })
  const { notify } = useToast()

  const webhookUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/tiny-webhook${brandCompanyId ? `?company=${brandCompanyId}` : ''}`

  function copyWebhookUrl() {
    navigator.clipboard.writeText(webhookUrl).then(() => {
      setWebhookCopied(true)
      setTimeout(() => setWebhookCopied(false), 2000)
    })
  }

  // ── Importação Excel/CSV ──────────────────────────────────────────────
  const [importFile, setImportFile] = useState<File | null>(null)
  const [previewCols, setPreviewCols] = useState<string[]>([])
  const [previewRows, setPreviewRows] = useState<ParsedRow[]>([])
  const [previewTotal, setPreviewTotal] = useState(0)
  const [fieldMap, setFieldMap] = useState<ImportFieldMap>({ sku: '', name: '', current_stock: '' })
  const [importing, setImporting] = useState(false)
  const [importResult, setImportResult] = useState<ImportResult | null>(null)
  const [importErrorsOpen, setImportErrorsOpen] = useState(false)

  function resetImport() {
    setImportFile(null)
    setPreviewCols([])
    setPreviewRows([])
    setPreviewTotal(0)
    setFieldMap({ sku: '', name: '', current_stock: '' })
    setImportResult(null)
    setImportErrorsOpen(false)
  }

  async function handleFileSelect(file: File | null) {
    if (!file) return
    setImportFile(file)
    setImportResult(null)
    try {
      const preview = await parseFilePreview(file)
      setPreviewCols(preview.columns)
      setPreviewRows(preview.rows)
      setPreviewTotal(preview.totalRows)
      // tentativa de auto-mapeamento por nomes comuns
      const find = (...candidates: string[]) =>
        preview.columns.find(c => candidates.some(cand => c.toLowerCase().includes(cand))) ?? ''
      setFieldMap({
        sku: find('sku', 'codigo', 'código'),
        name: find('produto', 'nome', 'descri'),
        current_stock: find('estoque', 'qtd', 'quantidade'),
        cost: find('custo', 'preço de custo'),
        monthly_avg_manual: find('média', 'media mensal'),
        sale_price: find('venda', 'preço de venda', 'preco'),
        supplier_name: find('fornecedor'),
      })
    } catch (e) {
      notify('error', `Falha ao ler arquivo: ${(e as Error).message}`)
      resetImport()
    }
  }

  async function runImport() {
    if (!importFile) return
    if (!fieldMap.sku || !fieldMap.name || !fieldMap.current_stock) {
      notify('error', 'Mapeie pelo menos SKU, Nome e Estoque atual.')
      return
    }
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return
    const { data: profile } = await supabase.from('profiles').select('company_id').eq('id', user.id).single()
    if (!profile) return

    const ext = importFile.name.toLowerCase().endsWith('.csv') ? 'csv' : 'xlsx'
    setImporting(true)
    try {
      const r = await importProducts({ file: importFile, fieldMap, companyId: profile.company_id, source: ext })
      setImportResult(r)
      notify('success', `${r.inserted} novos · ${r.updated} atualizados · ${r.skipped} pulados.`)
    } catch (e) {
      notify('error', `Erro: ${(e as Error).message}`)
    } finally {
      setImporting(false)
    }
  }

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (!user) return
      supabase.from('profiles').select('company_id').eq('id', user.id).single().then(({ data: profile }) => {
        if (!profile) return
        supabase.from('companies').select('tiny_api_token, tiny_v3_connected_at, primary_color, logo_url, id, erp_provider, sales_window_months, tiny_client_id, cadastro_recebimento_enabled').eq('id', profile.company_id).single().then(({ data }) => {
          if (data?.tiny_api_token) setToken(data.tiny_api_token as string)
          if (data?.primary_color) setBrandColor(data.primary_color)
          if (data?.logo_url) setBrandLogoUrl(data.logo_url as string)
          if (data?.id) { setBrandCompanyId(data.id as string); setV3CompanyId(data.id as string) }
          if (data?.tiny_v3_connected_at) setV3ConnectedAt(data.tiny_v3_connected_at as string)
          if (data?.erp_provider) setErpProvider(data.erp_provider as ErpProvider)
          if (typeof data?.sales_window_months === 'number') setSalesWindow(data.sales_window_months)
          if (data?.tiny_client_id) setTinyClientId(data.tiny_client_id as string)
          setCadastroRecEnabled(data?.cadastro_recebimento_enabled !== false)
        })
      })
    })
  }, [])

  useEffect(() => {
    const connected = searchParams.get('tiny_connected')
    const err = searchParams.get('tiny_error')
    if (connected) {
      setV3Msg({ type: 'success', text: 'Tiny V3 conectado com sucesso.' })
      const p = new URLSearchParams(searchParams); p.delete('tiny_connected'); setSearchParams(p, { replace: true })
    } else if (err) {
      setV3Msg({ type: 'error', text: `Falha ao conectar: ${err}` })
      const p = new URLSearchParams(searchParams); p.delete('tiny_error'); setSearchParams(p, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function saveTinyCredentials() {
    if (!v3CompanyId) { setCredentialsMsg({ type: 'error', text: 'Empresa não carregada, recarregue a página.' }); return }
    if (!tinyClientId.trim()) { setCredentialsMsg({ type: 'error', text: 'Informe o Client ID.' }); return }
    setSavingCredentials(true)
    setCredentialsMsg(null)
    const { error } = await supabase
      .from('companies')
      .update({
        tiny_client_id: tinyClientId.trim(),
        tiny_client_secret: tinyClientSecret.trim() || null,
      })
      .eq('id', v3CompanyId)
    setSavingCredentials(false)
    setCredentialsMsg(error
      ? { type: 'error', text: error.message }
      : { type: 'success', text: 'Credenciais salvas. Agora clique em "Conectar com Tiny".' })
    if (!error) setTinyClientSecret('') // limpa o secret da memória após salvar
  }

  function connectTinyV3() {
    if (!v3CompanyId) { setV3Msg({ type: 'error', text: 'Empresa não carregada, recarregue a página.' }); return }
    // Usa Client ID da empresa (salvo no banco) ou fallback para env var (compat BMS)
    const clientId = tinyClientId || (import.meta.env.VITE_TINY_CLIENT_ID as string | undefined)
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string
    if (!clientId) {
      setV3Msg({ type: 'error', text: 'Configure o Client ID do Tiny antes de conectar.' })
      return
    }
    const redirectUri = `${supabaseUrl}/functions/v1/tiny-oauth-callback`
    const authUrl = 'https://accounts.tiny.com.br/realms/tiny/protocol/openid-connect/auth'
    const params = new URLSearchParams({
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: 'openid',
      state: v3CompanyId,
    })
    window.location.href = `${authUrl}?${params.toString()}`
  }

  async function disconnectTinyV3() {
    if (!v3CompanyId) return
    if (!confirm('Desconectar Tiny V3? Você precisará reautorizar para sincronizar.')) return
    const { error } = await supabase
      .from('companies')
      .update({ tiny_access_token: null, tiny_refresh_token: null, tiny_token_expires_at: null, tiny_v3_connected_at: null })
      .eq('id', v3CompanyId)
    if (error) { setV3Msg({ type: 'error', text: error.message }); return }
    setV3ConnectedAt(null)
    setV3Msg({ type: 'success', text: 'Tiny V3 desconectado.' })
  }

  async function saveToken() {
    setSavingToken(true)
    setTokenMsg(null)
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) { setSavingToken(false); return }
    const { data: profile } = await supabase.from('profiles').select('company_id').eq('id', user.id).single()
    if (!profile) { setSavingToken(false); return }

    const { error } = await supabase
      .from('companies')
      .update({ tiny_api_token: token.trim() || null })
      .eq('id', profile.company_id)

    setTokenMsg(error
      ? { type: 'error', text: error.message }
      : { type: 'success', text: 'Token salvo.' })
    setSavingToken(false)
  }

  async function saveBranding() {
    if (!brandCompanyId) { setBrandMsg({ type: 'error', text: 'Empresa não carregada, recarregue a página.' }); return }
    setSavingBrand(true)
    setBrandMsg(null)
    let newLogoUrl = brandLogoUrl

    // Upload de logo se houver arquivo selecionado
    if (brandLogoFile) {
      const ext = brandLogoFile.name.split('.').pop() ?? 'png'
      const path = `${brandCompanyId}/logo.${ext}`
      const { error: upErr } = await supabase.storage
        .from('company-logos')
        // cacheControl de 1 ano: a logo aparece no header (toda navegação) e nos PDFs.
        // Cache longo evita re-download repetido do Storage (egress). O path é fixo por
        // empresa (logo.<ext>) + upsert, então o navegador reusa a cópia em cache.
        .upload(path, brandLogoFile, { upsert: true, contentType: brandLogoFile.type, cacheControl: '31536000' })
      if (upErr) {
        setBrandMsg({ type: 'error', text: `Falha no upload: ${upErr.message}` })
        setSavingBrand(false)
        return
      }
      const { data: urlData } = supabase.storage.from('company-logos').getPublicUrl(path)
      // Cache-busting: path fixo + cacheControl de 1 ano serviria a logo ANTIGA após uma troca.
      // O ?v= muda a cada upload → o navegador busca a nova uma vez e volta a cachear por 1 ano.
      newLogoUrl = `${urlData.publicUrl}?v=${Date.now()}`
    }

    const { error } = await supabase
      .from('companies')
      .update({ primary_color: brandColor, logo_url: newLogoUrl })
      .eq('id', brandCompanyId)

    if (error) {
      setBrandMsg({ type: 'error', text: error.message })
    } else {
      setBrandLogoUrl(newLogoUrl)
      setBrandLogoFile(null)
      setBrandLogoPreview(null)
      applyBranding(brandColor)
      setBrandMsg({ type: 'success', text: 'Visual da marca salvo com sucesso!' })
    }
    setSavingBrand(false)
  }

  function handleLogoFileChange(file: File | null) {
    if (!file) return
    setBrandLogoFile(file)
    const reader = new FileReader()
    reader.onload = e => setBrandLogoPreview(e.target?.result as string)
    reader.readAsDataURL(file)
  }

  function removeLogo() {
    setBrandLogoFile(null)
    setBrandLogoPreview(null)
    setBrandLogoUrl(null)
  }

  return (
    <AppLayout title="Configurações">
      <div className="max-w-screen-2xl">
        {/* Abas */}
        <div className="flex gap-1 border-b border-line mb-6 overflow-x-auto">
          {TABS.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`text-sm sm:text-base px-3 sm:px-5 py-2.5 border-b-2 transition-colors whitespace-nowrap shrink-0 ${
                tab === t.key
                  ? 'border-brand-500 text-ink'
                  : 'border-transparent text-ink-muted hover:text-ink'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'erp' && (
        <div className="space-y-6">
        {/* ── Saúde do sync (frescor por fonte) ─────────────────────── */}
        <SyncHealthCard />

        <section className="bg-surface border border-line rounded-xl p-6 space-y-6">
          {/* ── Banner do provider atual ─────────────────────────────── */}
          <div className="bg-sunken border border-line rounded-lg px-4 py-3 flex items-start gap-3">
            <div className="text-2xl shrink-0">
              {erpProvider === 'tiny' ? '🔌' : erpProvider === 'file' ? '📊' : '⚙️'}
            </div>
            <div className="flex-1">
              <div className="text-sm font-semibold text-ink mb-0.5">
                Integração atual: {erpProvider === 'tiny' ? 'Tiny ERP' : erpProvider === 'file' ? 'Planilha (CSV/Excel)' : 'Nenhuma'}
              </div>
              <p className="text-xs text-ink-muted">
                Outros providers em breve: Bling · Omie · Conta Azul · TOTVS · SAP B1.
                {' '}Pra trocar de integração, contate o suporte —
                {' '}<a href="mailto:contato@bx.tec.br" className="text-brand-400 hover:underline">contato@bx.tec.br</a>.
              </p>
            </div>
          </div>

          {/* ── Janela de histórico de vendas ────────────────────────── */}
          <div className="bg-sunken border border-line rounded-lg px-4 py-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="text-sm font-semibold text-ink mb-0.5">Histórico de vendas</div>
                <p className="text-xs text-ink-muted">
                  Quanto tempo de pedidos passados o sistema importa do ERP. Afeta cálculo de cobertura, ABC e encalhe.
                </p>
              </div>
              <select
                value={salesWindow}
                onChange={e => handleSalesWindowChange(Number(e.target.value))}
                disabled={savingWindow}
                className="bg-paper border border-line rounded-lg px-3 py-2 text-ink text-sm focus:outline-none focus:border-brand-500 disabled:opacity-50"
              >
                <option value={3}>3 meses</option>
                <option value={6}>6 meses (recomendado)</option>
                <option value={12}>12 meses</option>
                <option value={24}>24 meses</option>
              </select>
            </div>
            <p className="text-xs text-ink-faint mt-2">
              💡 Próximo sync respeitará a nova janela. Aumentar pode demorar alguns dias até completar (rate-limit do Tiny).
            </p>
          </div>

          {/* ── Cadastro e Recebimento ───────────────────────────── */}
          <div className="bg-sunken border border-line rounded-lg px-4 py-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-[260px]">
                <div className="text-sm font-semibold text-ink mb-0.5">Cadastro e Recebimento</div>
                <p className="text-xs text-ink-muted">
                  Aba para cadastro manual de produtos, recebimento com leitor de código de barras e importação de XML NFe.
                  {' '}<strong>Mutualmente exclusivo com o Tiny v3</strong>: se a empresa estiver integrada ao Tiny, o recurso fica indisponível para evitar duplicidade de cadastro.
                </p>
                {v3ConnectedAt && (
                  <p className="text-xs text-warn mt-1">
                    ⚠️ Tiny v3 conectado — o recurso está bloqueado independente deste toggle. Desconecte o Tiny para habilitar Cadastro e Recebimento.
                  </p>
                )}
              </div>
              <label className={`flex items-center gap-2 ${v3ConnectedAt ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                <input
                  type="checkbox"
                  checked={cadastroRecEnabled}
                  onChange={e => handleToggleCadastroRec(e.target.checked)}
                  disabled={savingCadastroRec || !!v3ConnectedAt}
                  className="w-4 h-4 accent-brand-500"
                />
                <span className="text-sm text-ink-muted">{cadastroRecEnabled ? 'Ativado' : 'Desativado'}</span>
              </label>
            </div>
          </div>

          {/* ── Tiny V3 (OAuth2) ─────────────────────────────────────── */}
          <div className="pb-6 border-b border-line">
            <div className="flex items-center gap-2 mb-1">
              <h2 className="text-lg font-semibold text-ink">Tiny V3 (OAuth2)</h2>
              <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded bg-brand-600/10 text-brand-700 dark:text-brand-300 border border-brand-600/30 dark:bg-brand-500/15 dark:text-brand-300 dark:border-brand-500/30">recomendado</span>
            </div>
            <p className="text-sm text-ink-faint mb-4">
              Autenticação segura via OAuth2. Tokens são renovados automaticamente.
            </p>

            {/* Credenciais do app OAuth (por empresa) */}
            <div className="mb-4 p-4 bg-sunken border border-line rounded-lg space-y-3">
              <p className="text-xs text-ink-muted">
                Informe as credenciais do seu app Tiny (<span className="text-ink">Tiny → Configurações → Aplicativos → Chaves de acesso</span>).
              </p>
              <div className="grid grid-cols-1 gap-3">
                <FormInput
                  label="Client ID"
                  type="text"
                  value={tinyClientId}
                  onChange={setTinyClientId}
                  placeholder="tiny-api-..."
                />
                <FormInput
                  label="Client Secret"
                  type="password"
                  value={tinyClientSecret}
                  onChange={setTinyClientSecret}
                  placeholder="Cole o Client Secret (deixe em branco para manter o atual)"
                />
              </div>
              {credentialsMsg && <Alert type={credentialsMsg.type} message={credentialsMsg.text} />}
              <button
                onClick={saveTinyCredentials}
                disabled={savingCredentials}
                className="px-4 py-2 bg-sunken hover:bg-line disabled:opacity-50 text-ink text-sm font-medium rounded-lg transition-colors"
              >
                {savingCredentials ? 'Salvando...' : 'Salvar credenciais'}
              </button>
            </div>

            {v3Msg && <div className="mb-3"><Alert type={v3Msg.type} message={v3Msg.text} /></div>}
            <div className="flex flex-wrap items-center gap-3">
              {v3ConnectedAt ? (
                <>
                  <span className="inline-flex items-center gap-2 text-sm text-brand-700 dark:text-brand-300">
                    <span className="w-2 h-2 rounded-full bg-emerald-500" />
                    Conectado em {new Date(v3ConnectedAt).toLocaleString('pt-BR')}
                  </span>
                  <button
                    onClick={disconnectTinyV3}
                    className="px-3 py-1.5 bg-danger/10 hover:bg-danger/20 text-danger text-xs font-medium rounded-lg transition-colors border border-danger/30"
                  >
                    Desconectar
                  </button>
                </>
              ) : (
                <>
                  <span className="inline-flex items-center gap-2 text-sm text-ink-muted">
                    <span className="w-2 h-2 rounded-full bg-line" />
                    Não conectado
                  </span>
                  <button
                    onClick={connectTinyV3}
                    className="px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    Conectar com Tiny
                  </button>
                </>
              )}
            </div>
          </div>

          <h2 className="text-lg font-semibold text-ink mb-1">Integração ERP (V2 — legado)</h2>
          <p className="text-sm text-ink-faint mb-4">
            Cole o token de API do seu ERP. O sistema usa para sincronizar produtos, estoque e custo.
          </p>

          <div className="space-y-4">
            <FormInput
              label="Token de API"
              type="password"
              value={token}
              onChange={setToken}
              placeholder="Cole aqui o token gerado no painel do ERP"
            />
            {tokenMsg && <Alert type={tokenMsg.type} message={tokenMsg.text} />}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={saveToken}
                disabled={savingToken}
                className="px-4 py-2 bg-base-600 hover:bg-base-500 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
              >
                {savingToken ? 'Salvando...' : 'Salvar Token'}
              </button>
              <button
                onClick={() => syncNow(true)}
                disabled={syncing}
                className="px-4 py-2 bg-sunken hover:bg-line disabled:opacity-50 text-ink text-sm font-medium rounded-lg transition-colors"
              >
                {syncing ? 'Aguarde...' : 'Testar Conexão'}
              </button>
              <button
                onClick={() => syncNow(false)}
                disabled={syncing}
                className="px-4 py-2 bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
              >
                {syncing ? 'Sincronizando...' : 'Sincronizar Agora'}
              </button>
            </div>
            {result && (
              <Alert
                type={result.ok ? 'success' : 'error'}
                message={result.ok
                  ? `Sincronização concluída · ${result.inserted ?? 0} produtos · ${result.stockUpdated ?? 0} estoques atualizados`
                  : `Erro: ${result.error ?? 'desconhecido'}`}
              />
            )}
          </div>
        </section>
        </div>
        )}

        {tab === 'webhook' && (
        <section className="bg-surface border border-line rounded-xl p-6">
          <h2 className="text-lg font-semibold text-ink mb-1">Webhook de Estoque em Tempo Real</h2>
          <p className="text-sm text-ink-faint mb-4">
            Configure esta URL no seu ERP para receber atualizações de estoque automaticamente, sem precisar sincronizar manualmente.
          </p>
          <div className="flex items-center gap-2">
            <input
              readOnly
              value={webhookUrl}
              className="flex-1 bg-paper border border-line text-ink-muted text-sm rounded-lg px-3 py-2 font-mono select-all"
              onFocus={e => e.currentTarget.select()}
            />
            <button
              onClick={copyWebhookUrl}
              className="px-4 py-2 bg-sunken hover:bg-line text-ink text-sm font-medium rounded-lg transition-colors whitespace-nowrap"
            >
              {webhookCopied ? 'Copiado!' : 'Copiar'}
            </button>
          </div>
          <div className="mt-3 text-xs text-ink-faint space-y-1">
            <p>Caminho no Tiny ERP: <span className="text-ink-muted">Configurações → geral → Outras configurações → Webhooks → "Receber notificações de lançamentos de estoque"</span></p>
            <p>Eventos recomendados: <span className="text-ink-muted">Lançamentos de estoque · Alterações de produto</span></p>
            <p className="text-[#9a5a28] dark:text-amber-300">Esta URL é exclusiva desta empresa — use a URL gerada em cada conta separadamente.</p>
          </div>
        </section>
        )}

        {tab === 'importar' && (
        <section className="bg-surface border border-line rounded-xl p-6">
          <h2 className="text-lg font-semibold text-ink mb-1">Importar Produtos por Planilha</h2>
          <p className="text-sm text-ink-faint mb-4">
            Use esta opção se você não usa um ERP integrado. Aceita <span className="text-ink-muted">.xlsx</span>, <span className="text-ink-muted">.xls</span> e <span className="text-ink-muted">.csv</span>.
            Colunas obrigatórias: SKU, Nome e Estoque atual. Produtos com SKU já existente são <strong>atualizados</strong>; SKUs novos são <strong>criados</strong>.
          </p>

          {/* Etapa 1: upload */}
          {!importFile && (
            <div className="border-2 border-dashed border-line rounded-lg p-8 text-center">
              <input
                id="import-file"
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={e => handleFileSelect(e.target.files?.[0] ?? null)}
                className="hidden"
              />
              <label
                htmlFor="import-file"
                className="inline-block px-4 py-2 bg-base-600 hover:bg-base-500 text-white text-sm font-medium rounded-lg cursor-pointer transition-colors"
              >
                Selecionar arquivo
              </label>
              <p className="text-xs text-ink-faint mt-3">
                O arquivo deve ter cabeçalhos na primeira linha.
              </p>
            </div>
          )}

          {/* Etapa 2: preview + mapeamento */}
          {importFile && !importResult && (
            <div className="space-y-4">
              <div className="flex items-center justify-between bg-sunken rounded-lg px-3 py-2">
                <div className="text-sm">
                  <span className="text-ink font-medium">{importFile.name}</span>
                  <span className="text-ink-faint ml-2">· {previewTotal} linhas detectadas</span>
                </div>
                <button onClick={resetImport} className="text-xs text-ink-muted hover:text-ink">
                  Trocar arquivo
                </button>
              </div>

              {previewRows.length > 0 && (
                <div className="overflow-x-auto border border-line rounded-lg">
                  <table className="w-full text-xs">
                    <thead className="bg-sunken">
                      <tr>
                        {previewCols.map(c => (
                          <th key={c} className="text-left text-[11px] font-semibold text-ink-muted uppercase px-3 py-2 whitespace-nowrap">{c}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewRows.map((r, i) => (
                        <tr key={i} className="border-t border-line">
                          {previewCols.map(c => (
                            <td key={c} className="px-3 py-1.5 text-ink-muted whitespace-nowrap">{r[c] != null ? String(r[c]) : ''}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div>
                <h3 className="text-sm font-semibold text-ink mb-2">Mapeie as colunas</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[
                    { key: 'sku',                label: 'SKU',                required: true },
                    { key: 'name',               label: 'Nome do produto',     required: true },
                    { key: 'current_stock',      label: 'Estoque atual',       required: true },
                    { key: 'cost',               label: 'Custo',               required: false },
                    { key: 'monthly_avg_manual', label: 'Média mensal',        required: false },
                    { key: 'sale_price',         label: 'Preço de venda',      required: false },
                    { key: 'supplier_name',      label: 'Fornecedor (nome)',   required: false },
                  ].map(({ key, label, required }) => (
                    <div key={key}>
                      <label className="block text-[10px] text-ink-faint uppercase mb-1">
                        {label} {required && <span className="text-danger">*</span>}
                      </label>
                      <select
                        value={(fieldMap as unknown as Record<string, string | undefined>)[key] ?? ''}
                        onChange={e => setFieldMap(prev => ({ ...prev, [key]: e.target.value || undefined }) as ImportFieldMap)}
                        className="w-full bg-paper border border-line rounded px-2 py-1.5 text-ink text-sm"
                      >
                        <option value="">— não importar —</option>
                        {previewCols.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={runImport}
                  disabled={importing}
                  className="px-4 py-2 bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg"
                >
                  {importing ? `Importando ${previewTotal} linhas...` : `Importar ${previewTotal} produto(s)`}
                </button>
                <button onClick={resetImport} className="px-4 py-2 bg-sunken hover:bg-line text-ink-muted text-sm rounded-lg">
                  Cancelar
                </button>
              </div>
            </div>
          )}

          {/* Etapa 3: resultado */}
          {importResult && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-brand-600/10 border border-brand-600/30 rounded-lg p-3 text-center dark:bg-brand-500/15 dark:border-brand-500/30">
                  <p className="text-2xl font-bold text-brand-700 dark:text-brand-300">{importResult.inserted}</p>
                  <p className="text-xs text-brand-700 dark:text-brand-300 uppercase dark:text-brand-300">Novos</p>
                </div>
                <div className="bg-sky-100 border border-sky-300 rounded-lg p-3 text-center dark:bg-sky-500/10 dark:border-sky-500/30">
                  <p className="text-2xl font-bold text-sky-800 dark:text-sky-300">{importResult.updated}</p>
                  <p className="text-xs text-sky-800 uppercase dark:text-sky-300">Atualizados</p>
                </div>
                <div className="bg-warn/15 border border-warn/40 rounded-lg p-3 text-center dark:bg-warn/20">
                  <p className="text-2xl font-bold text-[#9a5a28] dark:text-amber-300">{importResult.skipped}</p>
                  <p className="text-xs text-[#9a5a28] uppercase dark:text-amber-300">Pulados</p>
                </div>
              </div>

              {importResult.errors.length > 0 && (
                <div className="border border-line rounded-lg overflow-hidden">
                  <button
                    onClick={() => setImportErrorsOpen(o => !o)}
                    className="w-full px-3 py-2 bg-sunken hover:bg-line text-left text-sm text-ink-muted flex items-center justify-between"
                  >
                    <span>{importResult.errors.length} erro(s) — clique para ver</span>
                    <span className="text-xs">{importErrorsOpen ? '▲' : '▼'}</span>
                  </button>
                  {importErrorsOpen && (
                    <div className="max-h-60 overflow-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-sunken sticky top-0">
                          <tr>
                            <th className="text-left px-3 py-1.5 text-ink-muted">Linha</th>
                            <th className="text-left px-3 py-1.5 text-ink-muted">SKU</th>
                            <th className="text-left px-3 py-1.5 text-ink-muted">Motivo</th>
                          </tr>
                        </thead>
                        <tbody>
                          {importResult.errors.map((e, i) => (
                            <tr key={i} className="border-t border-line">
                              <td className="px-3 py-1 text-ink-faint">{e.row || '—'}</td>
                              <td className="px-3 py-1 font-mono text-ink-muted">{e.sku || '—'}</td>
                              <td className="px-3 py-1 text-danger">{e.reason}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              <button onClick={resetImport} className="px-4 py-2 bg-base-600 hover:bg-base-500 text-white text-sm font-medium rounded-lg">
                Importar outro arquivo
              </button>
            </div>
          )}
        </section>
        )}


        {tab === 'branding' && (
        <section className="bg-surface border border-line rounded-xl p-6 max-w-lg">
          <h2 className="text-lg font-semibold text-ink mb-1">Visual da Marca</h2>
          <p className="text-sm text-ink-faint mb-6">
            Personalize a cor e o logo exibidos no app e nos PDFs de pedido.
          </p>

          {/* Logo */}
          <div className="mb-6">
            <label className="block text-xs font-semibold text-ink-muted uppercase tracking-wide mb-3">Logo da Empresa</label>
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-lg bg-sunken border border-line flex items-center justify-center overflow-hidden shrink-0">
                {brandLogoPreview || brandLogoUrl
                  ? <img src={brandLogoPreview ?? brandLogoUrl!} alt="Logo" className="w-full h-full object-contain p-1" />
                  : <span className="text-ink-faint text-3xl">🏢</span>
                }
              </div>
              <div className="flex flex-col gap-2">
                <label className="inline-block px-3 py-1.5 bg-sunken hover:bg-line text-ink text-sm font-medium rounded-lg cursor-pointer transition-colors">
                  {brandLogoPreview || brandLogoUrl ? 'Trocar logo' : 'Enviar logo'}
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/svg+xml,image/webp"
                    className="hidden"
                    onChange={e => handleLogoFileChange(e.target.files?.[0] ?? null)}
                  />
                </label>
                {(brandLogoPreview || brandLogoUrl) && (
                  <button
                    onClick={removeLogo}
                    className="text-xs text-danger hover:text-danger/80 text-left"
                  >
                    Remover logo
                  </button>
                )}
                <p className="text-xs text-ink-faint">PNG, JPG, SVG ou WebP · máx 2 MB</p>
              </div>
            </div>
          </div>

          {/* Cor primária */}
          <div className="mb-6">
            <label className="block text-xs font-semibold text-ink-muted uppercase tracking-wide mb-3">Cor Primária</label>
            <div className="flex items-center gap-4">
              <input
                type="color"
                value={brandColor}
                onChange={e => {
                  setBrandColor(e.target.value)
                  applyBranding(e.target.value)
                }}
                className="w-14 h-10 rounded-lg cursor-pointer bg-transparent border-0 p-0.5"
              />
              <div className="flex-1 flex items-center gap-3">
                <input
                  type="text"
                  value={brandColor}
                  onChange={e => {
                    const v = e.target.value
                    setBrandColor(v)
                    if (/^#[0-9a-f]{6}$/i.test(v)) applyBranding(v)
                  }}
                  placeholder="#647B45"
                  maxLength={7}
                  className="w-28 bg-paper border border-line rounded-lg px-3 py-2 text-ink text-sm font-mono focus:outline-none focus:border-brand-500"
                />
                <button
                  onClick={() => { setBrandColor('#647B45'); resetBranding() }}
                  className="text-xs text-ink-faint hover:text-ink-muted transition-colors"
                >
                  Resetar padrão
                </button>
              </div>
            </div>

            {/* Preview de botões */}
            <div className="mt-4 p-4 bg-sunken rounded-lg">
              <p className="text-xs text-ink-faint mb-3 uppercase tracking-wide">Preview</p>
              <div className="flex flex-wrap gap-2">
                <button className="px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium rounded-lg transition-colors">
                  Botão primário
                </button>
                <button className="px-4 py-2 border-2 border-brand-600 text-brand-600 dark:text-brand-300 text-sm font-medium rounded-lg hover:bg-brand-600/10 transition-colors">
                  Botão outline
                </button>
                <span className="px-2 py-1 bg-brand-600/20 text-brand-300 text-xs rounded font-medium">Badge</span>
              </div>
            </div>
          </div>

          {brandMsg && <Alert type={brandMsg.type} message={brandMsg.text} />}

          <button
            onClick={saveBranding}
            disabled={savingBrand}
            className="mt-4 px-5 py-2.5 bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
          >
            {savingBrand ? 'Salvando...' : 'Salvar visual'}
          </button>
        </section>
        )}

        {tab === 'usuarios' && (
        <section className="bg-surface border border-line rounded-xl p-6">
          <h2 className="text-lg font-semibold text-ink mb-1">Usuários da empresa</h2>
          <p className="text-sm text-ink-faint mb-4">
            {team.loading
              ? 'Carregando...'
              : `${team.users.length} de ${team.maxUsers} usuários · ${isAdmin ? 'você pode convidar até ' + (team.maxUsers - team.users.length) + ' a mais' : 'somente administradores podem convidar'}.`}
          </p>
          {isAdmin && (
            <p className="text-sm text-ink-faint mb-4">
              Precisa de mais usuários?{' '}
              <Link to="/planos" className="text-brand-400 hover:text-brand-300 font-medium">
                Ver planos
              </Link>
            </p>
          )}

          {isAdmin && (
            <div className="mb-6 p-4 bg-sunken border border-line rounded-lg">
              <h3 className="text-sm font-semibold text-ink mb-3">Convidar novo usuário</h3>
              <div className="flex flex-wrap gap-2 items-end">
                <div className="flex-1 min-w-[240px]">
                  <label className="block text-[10px] text-ink-faint uppercase mb-1">E-mail</label>
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={e => setInviteEmail(e.target.value)}
                    placeholder="convidado@empresa.com"
                    className="w-full bg-paper border border-line rounded px-3 py-2 text-ink text-sm"
                  />
                </div>
                <div>
                  <label className="block text-[10px] text-ink-faint uppercase mb-1">Função</label>
                  <select
                    value={inviteRole}
                    onChange={e => setInviteRole(e.target.value as 'operator' | 'admin')}
                    className="bg-paper border border-line rounded px-3 py-2 text-ink text-sm"
                  >
                    <option value="operator">Operador</option>
                    <option value="admin">Administrador</option>
                  </select>
                </div>
                <button
                  onClick={handleInvite}
                  disabled={inviting || !team.canInviteMore}
                  className="px-4 py-2 bg-base-600 hover:bg-base-500 disabled:opacity-40 text-white text-sm font-medium rounded-lg transition-colors"
                  title={!team.canInviteMore ? `Limite de ${team.maxUsers} usuários atingido` : ''}
                >
                  {inviting ? 'Enviando...' : 'Convidar'}
                </button>
              </div>
              <p className="mt-2 text-xs text-ink-faint">
                O convidado receberá um e-mail com link para definir a senha e acessar o sistema.
                <br />
                <span className="text-ink-muted">Operador</span>: acessa Painel, Pedidos e Dashboard. Não edita Configurações.
                <br />
                <span className="text-ink-muted">Administrador</span>: acesso total, inclusive convidar outros usuários.
              </p>
            </div>
          )}

          {team.error && <Alert type="error" message={team.error} />}

          <div className="overflow-auto border border-line rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-sunken border-b border-line">
                <tr>
                  <th className="text-left text-xs font-semibold text-ink-muted uppercase px-4 py-2">E-mail</th>
                  <th className="text-left text-xs font-semibold text-ink-muted uppercase px-4 py-2">Função</th>
                  <th className="text-left text-xs font-semibold text-ink-muted uppercase px-4 py-2">Status</th>
                  <th className="text-left text-xs font-semibold text-ink-muted uppercase px-4 py-2">Cadastro</th>
                  {isAdmin && <th className="text-right text-xs font-semibold text-ink-muted uppercase px-4 py-2">Ação</th>}
                </tr>
              </thead>
              <tbody>
                {team.users.map(u => (
                  <tr key={u.id} className="border-b border-line hover:bg-sunken/50">
                    <td className="px-4 py-2 text-ink">
                      {u.email}
                      {u.id === team.myId && <span className="ml-2 text-[10px] text-ink-faint uppercase">(você)</span>}
                    </td>
                    <td className="px-4 py-2 text-ink-muted capitalize">{u.role}</td>
                    <td className="px-4 py-2">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                        u.onboarded
                          ? 'bg-brand-600/10 text-brand-700 dark:text-brand-300 border border-brand-600/30 dark:bg-brand-500/15 dark:text-brand-300 dark:border-brand-500/30'
                          : 'bg-warn/15 text-[#9a5a28] border border-warn/40 dark:bg-warn/20 dark:text-amber-300'
                      }`}>
                        {u.onboarded ? 'Ativo' : 'Pendente'}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-ink-muted text-xs">
                      {new Date(u.created_at).toLocaleDateString('pt-BR')}
                    </td>
                    {isAdmin && (
                      <td className="px-4 py-2 text-right">
                        {u.id !== team.myId
                         && !(u.role === 'superadmin' && team.myRole !== 'superadmin') && (
                          <button
                            onClick={() => handleRemoveUser(u.id, u.email)}
                            className="text-xs px-3 py-1 bg-danger/10 hover:bg-danger/20 border border-danger/30 rounded text-danger"
                          >
                            Remover
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
                {team.users.length === 0 && !team.loading && (
                  <tr><td colSpan={isAdmin ? 5 : 4} className="px-4 py-8 text-center text-ink-faint">
                    Nenhum usuário.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
        )}

        {tab === 'filiais' && <FiliaisSettings />}

        {tab === 'conta' && (
        <section className="bg-surface border border-line rounded-xl p-6 max-w-lg space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-ink mb-1">Minha conta</h2>
            <p className="text-sm text-ink-faint">Altere sua senha de acesso.</p>
          </div>
          <div>
            <label className="block text-xs text-ink-faint uppercase mb-1">E-mail</label>
            <div className="text-sm text-ink-muted">{user?.email ?? '—'}</div>
          </div>
          <FormInput
            label="Nova senha"
            type="password"
            value={newPwd}
            onChange={setNewPwd}
            placeholder="Mínimo 6 caracteres"
          />
          <FormInput
            label="Confirmar nova senha"
            type="password"
            value={confirmPwd}
            onChange={setConfirmPwd}
          />
          <button
            onClick={handleChangePassword}
            disabled={savingPwd || !newPwd || !confirmPwd}
            className="px-4 py-2 bg-base-600 hover:bg-base-500 disabled:opacity-40 text-white text-sm font-medium rounded-lg transition-colors"
          >
            {savingPwd ? 'Salvando...' : 'Salvar nova senha'}
          </button>

          {/* ── LGPD: direitos do titular ─────────────────────────────────── */}
          <div className="pt-6 mt-2 border-t border-line">
            <h3 className="text-sm font-semibold text-ink mb-1">Seus dados (LGPD)</h3>
            <p className="text-xs text-ink-faint mb-4">
              Exerça seus direitos como titular: exporte uma cópia dos seus dados ou solicite exclusão da conta.
              Detalhes em <a href="/privacidade" target="_blank" rel="noopener noreferrer" className="text-brand-400 hover:underline">Política de Privacidade</a>.
            </p>

            <button
              onClick={handleExportData}
              disabled={exporting}
              className="w-full sm:w-auto px-4 py-2 mb-3 bg-sunken hover:bg-line disabled:opacity-40 text-ink text-sm font-medium rounded-lg transition-colors"
            >
              {exporting ? 'Exportando...' : '⬇ Exportar meus dados (JSON)'}
            </button>

            <div className="mt-2">
              <button
                onClick={handleRequestDeletion}
                disabled={deletingAccount}
                className="w-full sm:w-auto px-4 py-2 bg-danger/10 hover:bg-danger/20 border border-danger/30 disabled:opacity-40 text-danger text-sm font-medium rounded-lg transition-colors"
              >
                {deletingAccount ? 'Processando...' : '🗑 Excluir minha conta'}
              </button>
              <p className="text-xs text-ink-faint mt-2">
                Grace period de 30 dias antes da exclusão definitiva. Você pode reverter dentro desse prazo.
              </p>
            </div>
          </div>
        </section>
        )}

        {tab === 'sobre' && (
        <section className="bg-surface border border-line rounded-xl p-6 max-w-2xl space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-ink mb-1">Sobre esta versão</h2>
            <p className="text-sm text-ink-faint">
              Informações da versão em produção. Útil para suporte e troubleshooting.
            </p>
          </div>
          <dl className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
            <div>
              <dt className="text-xs text-ink-faint uppercase mb-1">Versão</dt>
              <dd className="text-ink font-mono tabular-nums">v{APP_VERSION}</dd>
            </div>
            <div>
              <dt className="text-xs text-ink-faint uppercase mb-1">Commit</dt>
              <dd className="text-ink font-mono">
                <a
                  href={`https://github.com/isaiasivision-a11y/bx-solutions/commit/${GIT_COMMIT}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-400 hover:underline"
                >
                  {GIT_COMMIT}
                </a>
              </dd>
            </div>
            <div>
              <dt className="text-xs text-ink-faint uppercase mb-1">Build</dt>
              <dd className="text-ink tabular-nums">
                {new Date(BUILD_DATE).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}
              </dd>
            </div>
          </dl>
          <div className="pt-4 mt-2 border-t border-line text-xs text-ink-faint space-y-2">
            <p>
              <a
                href="https://github.com/isaiasivision-a11y/bx-solutions/blob/main/CHANGELOG.md"
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-400 hover:underline"
              >
                📋 Ver changelog completo
              </a>
            </p>
            <p>
              Em caso de problema, contate{' '}
              <a href="mailto:contato@bx.tec.br" className="text-brand-400 hover:underline">contato@bx.tec.br</a>
              {' '}informando a versão acima.
            </p>
          </div>
        </section>
        )}
      </div>
    </AppLayout>
  )
}
